<script lang="ts" setup>
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/utilities/constants.ts';
import ProjectCarousel from '@/views/projects/ProjectCarousel.vue';
import ProjectModelCharacteristics from '@/views/projects/ProjectModelCharacteristics.vue';
import ProjectDescription from '@/views/projects/ProjectDescription.vue';
import ProjectCalculator from '@/views/projects/ProjectCalculator.vue';
import PhoneButton from '@/components/PhoneButton.vue';
import ContactForm from '@/components/ContactForm.vue';
import type { GetProjectDetailsProjectResponse } from '@/api';
import type { ProjectCarouselImage } from '@/views/projects/types.ts';

const props = defineProps<{
  project: GetProjectDetailsProjectResponse;
  modelId: number;
  phoneNumber?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t, locale } = useI18n();

const model = computed(() => props.project.models.find((m) => m.id === props.modelId) ?? null);

const modelImages = computed<ProjectCarouselImage[]>(() => {
  const m = model.value;
  if (!m) return [];

  return m.images.map((image) => ({ src: image.src, title: image.title, section: m.name }));
});

const showCalculator = computed(() => {
  const m = model.value;
  return m !== null && m.price.type.id === Constants.PriceTypeId.ForSale && m.price.amount > 0;
});

const contactSection = useTemplateRef('contactSection');

const scrollToContactForm = () => {
  contactSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
</script>

<template>
  <!-- Le montage conditionnel remplace TransitionRoot : les transitions de Headless UI restaient
       bloquées à leur état de départ lorsqu'un clic ouvrait le popup, et la couche de fermeture
       ne se retirait jamais, ce qui interceptait ensuite tous les clics de la page. -->
  <Dialog v-if="model" :open="true" class="no-print relative z-40" @close="emit('close')">
    <div aria-hidden="true" class="animate-dialog-backdrop fixed inset-0 bg-black/50" />

    <div class="fixed inset-0 z-40 overflow-y-auto">
      <div class="flex min-h-full justify-center sm:p-4">
        <DialogPanel class="animate-dialog-panel relative h-fit w-full max-w-4xl bg-white shadow-xl sm:rounded-xl">
          <div class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-4 py-3 sm:rounded-t-xl">
            <div class="min-w-0">
              <DialogTitle class="truncate text-lg font-bold tracking-tight text-gray-900 md:text-xl lg:text-2xl">{{ model.name }}</DialogTitle>
              <p class="truncate text-xs text-gray-700 md:text-sm">{{ project.name }}, {{ project.address.city.name }}</p>
            </div>
            <button class="focus:outline-ph-green-600 hover:text-ph-green-600 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md p-2 text-gray-400" type="button" @click="emit('close')">
              <span class="sr-only">{{ t('views.project.model-dialog.close') }}</span>
              <XMarkIcon aria-hidden="true" class="size-6" />
            </button>
          </div>

          <div class="space-y-8 px-4 py-6 md:space-y-10">
            <ProjectCarousel v-if="modelImages.length > 0" :images="modelImages" />

            <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
              <span class="bg-ph-green-50 text-ph-green-700 rounded-md px-2 py-1 text-xs font-semibold md:text-sm">{{ model.category.name }}</span>
              <div v-if="project.isPaid" class="text-gray-700">
                <template v-if="model.price.amount > 0">
                  <p>
                    <span v-if="model.price.isStarting" class="pr-1 text-sm">{{ t('views.project.models.starting-from') }}</span>
                    <strong class="text-lg md:text-xl">{{ model.price.amount.formatCurrency(locale) }}</strong>
                    <span v-if="model.price.type.id == Constants.PriceTypeId.ForRent" class="pl-1 text-sm">{{ t('views.project.models.per-month') }}</span>
                  </p>
                  <p class="text-xs">
                    <span v-if="model.price.priceTaxes === 'included'">{{ t('views.project.models.taxes-included') }}</span>
                    <span v-if="model.price.priceTaxes === 'excluded'">{{ t('views.project.models.taxes-excluded') }}</span>
                  </p>
                </template>
                <p v-else>{{ t('views.project.models.upon-request') }}</p>
              </div>
            </div>

            <ProjectModelCharacteristics :modelId="model.id" :project="project" />
            <ProjectDescription :modelId="model.id" :project="project" />
            <ProjectCalculator v-if="showCalculator" :modelId="model.id" :project="project" />

            <div ref="contactSection" class="scroll-mt-16">
              <ContactForm :header="t('views.project.model-dialog.contact')" :project="project" />
            </div>
          </div>

          <div class="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:rounded-b-xl">
            <button class="hover:bg-ph-green-600 bg-ph-green-700 focus:outline-ph-green-600 cursor-pointer rounded-lg px-4 py-2 text-white focus:outline-2 focus:outline-offset-2" type="button" @click="scrollToContactForm">
              {{ t('views.project.model-dialog.contact') }}
            </button>
            <PhoneButton v-if="phoneNumber" :phone-number="phoneNumber" :target-id="model.id" target="projectModel" />
          </div>
        </DialogPanel>
      </div>
    </div>
  </Dialog>
</template>

<style lang="css" scoped></style>
